import './App.css';
import React, { useState, useEffect, useRef } from 'react';
import { BackIcon, RefreshIcon } from './Icons';
import { shuffle } from './utils';

function getWrongOptions(allWords, correctAnswer, key) {
  const wrongAnswers = [];
  const filteredWords = allWords.filter(word => word[key] !== correctAnswer[key]);

  while (wrongAnswers.length < 2 && filteredWords.length > 0) {
    const randomIndex = Math.floor(Math.random() * filteredWords.length);
    const randomWord = filteredWords.splice(randomIndex, 1)[0];
    if (randomWord) wrongAnswers.push(randomWord[key]);
  }
  return wrongAnswers;
}

export default function MultipleChoiceApp({ words, onBack }) {
  const [shuffledWords, setShuffledWords] = useState(() => shuffle(words));
  const [currentIndex, setCurrentIndex] = useState(0);
  const [options, setOptions] = useState([]);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [isCorrect, setIsCorrect] = useState(null);
  const [score, setScore] = useState(0);
  const [completed, setCompleted] = useState(false);
  const [timeLeft, setTimeLeft] = useState(5);
  const timerRef = useRef(null);
  const handleTimeoutRef = useRef(null);
  const [isHardMode, setIsHardMode] = useState(false);
  const [languageDirection, setLanguageDirection] = useState('pl-de');

  const currentWord = shuffledWords[currentIndex];
  const sourceLang = languageDirection === 'pl-de' ? 'pl' : 'de';
  const targetLang = languageDirection === 'pl-de' ? 'de' : 'pl';

  useEffect(() => {
    if (currentWord) {
      const wrongOptions = getWrongOptions(words, currentWord, targetLang);
      setOptions(shuffle([currentWord[targetLang], ...wrongOptions]));
    }
  }, [currentWord, words, targetLang]);

  const toggleLanguageDirection = () => {
    setLanguageDirection(prev => prev === 'pl-de' ? 'de-pl' : 'pl-de');
    setSelectedAnswer(null);
    setIsCorrect(null);
  };

  const toggleHardMode = () => {
    setIsHardMode(prev => !prev);
    setSelectedAnswer(null);
    setIsCorrect(null);
  };

  useEffect(() => {
    if (!isHardMode || selectedAnswer || completed) { clearInterval(timerRef.current); return; }
    setTimeLeft(5);
    timerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) { clearInterval(timerRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [currentIndex, selectedAnswer, completed, isHardMode]);

  useEffect(() => {
    if (isHardMode && timeLeft === 0 && !selectedAnswer && !completed) {
      handleTimeoutRef.current();
    }
  }, [timeLeft, isHardMode, selectedAnswer, completed]);

  const handleAnswerClick = (answer) => {
    if (selectedAnswer) return;
    clearInterval(timerRef.current);

    setSelectedAnswer(answer);
    if (answer === currentWord[targetLang]) {
      setIsCorrect(true);
      setTimeout(() => handleNext(), 800);
    } else {
      setIsCorrect(false);
      setScore(s => s + 1);
    }
  };

  const handleTimeout = () => {
    if (selectedAnswer) return;
    setSelectedAnswer('timeout');
    setIsCorrect(false);
    setScore(s => s + 1);
  };

  handleTimeoutRef.current = handleTimeout;

  const handleNext = () => {
    if (currentIndex + 1 < shuffledWords.length) {
      setCurrentIndex(currentIndex + 1);
      setSelectedAnswer(null);
      setIsCorrect(null);
    } else {
      setCompleted(true);
    }
  };

  const restart = () => {
    setShuffledWords(shuffle(words));
    setCurrentIndex(0);
    setSelectedAnswer(null);
    setIsCorrect(null);
    setScore(0);
    setCompleted(false);
  };

  if (completed) {
    return (
      <>
        <div className="flashcard-container">
          <div className="completion-header">
            <div className="completion-tag">Ukończono</div>
            <div className="completion-title">Test wyboru</div>
          </div>
          <div className="score-block">
            <div className={`score-number ${score === 0 ? 'perfect' : ''}`}>{score}</div>
            <div className="score-label">Punkty karne</div>
          </div>
          {score === 0 && <p className="feedback-correct">Bezbłędnie — doskonale!</p>}
          <button className="btn-primary" onClick={restart}><RefreshIcon /> Powtórz</button>
        </div>
        <div className="floating-back-button"><button onClick={onBack}><BackIcon /></button></div>
      </>
    );
  }

  return (
    <>
      <div className="flashcard-container">
        <div className="progress-container">
          <div className="progress-bar" style={{ width: `${Math.round((currentIndex + 1) / shuffledWords.length * 100)}%` }} />
        </div>
        {isHardMode && (
          <div className={`timer ${timeLeft <= 1 ? 'danger' : ''}`}>{timeLeft}</div>
        )}
        <h1>Test wyboru</h1>
        <div className="card-counter">{currentIndex + 1} / {shuffledWords.length}</div>

        <div className="language-toggle-container">
          <button onClick={toggleLanguageDirection} className="language-toggle-button">
            {languageDirection === 'pl-de' ? 'PL → DE' : 'DE → PL'}
          </button>
        </div>

        <div className="word-label">{sourceLang === 'pl' ? 'Polskie słowo' : 'Deutsches Wort'}</div>
        <div className="word-prompt">{currentWord[sourceLang]}</div>

        {selectedAnswer === 'timeout' && (
          <p className="feedback-wrong">Czas minął!</p>
        )}

        <div className="multiple-choice-options">
          {options.map((option, i) => (
            <button
              key={i}
              onClick={() => handleAnswerClick(option)}
              className={`choice-button ${selectedAnswer
                ? option === currentWord[targetLang] ? 'correct'
                : option === selectedAnswer ? 'incorrect' : ''
                : ''}`}
              disabled={!!selectedAnswer}
            >
              {option}
            </button>
          ))}
        </div>

        {selectedAnswer && !isCorrect && (
          <button className="btn-primary" onClick={handleNext} style={{ marginTop: '1.25rem' }}>
            Następne
          </button>
        )}

        <button onClick={toggleHardMode} className="hard-mode-toggle">
          {isHardMode ? 'Hard' : 'Normal'}
        </button>
      </div>
      <div className="floating-back-button"><button onClick={onBack}><BackIcon /></button></div>
    </>
  );
}
